import { ref, type Ref } from 'vue'
import type { ICitaAdmision } from '@/modules/Admision/interfaces'
import type {
  IFormComponent,
} from '@/modules/Administracion/interfaces'
import { formCompValueService } from '@/services'

// Extender la interfaz para incluir los campos adicionales
interface IExtendedFormComponent extends IFormComponent {
  codigo?: string
  tecnologia?: string
}

interface UseAdmisionTechComponentsReturn {
  components: Ref<IFormComponent[]>
  extractTechComponents: (admision: ICitaAdmision | undefined, estadosFiltro?: string[]) => Promise<IFormComponent[]>
  clearComponents: () => void
  getComponents: () => IFormComponent[]
  getComponentsCount: () => number
  getComponentsByTechCode: (codigo: string) => IFormComponent[]
  getComponentsByType: (type: string) => IFormComponent[]
}

/**
 * Composable para manejar la extracción y mapeo de componentes de tecnologías de salud
 * de una admisión médica.
 */
export const useAdmisionTechComponents = (): UseAdmisionTechComponentsReturn => {
  const components = ref<IFormComponent[]>([])

  /**
   * Extrae y mapea los componentes de formulario de las tecnologías tramitadas
   * de una admisión médica.
   *
   * @param admision - La admisión médica de la cual extraer los componentes
   * @param estadosFiltro - Estados de tecnología a incluir (por defecto: ['TRAMITADO'])
   * @returns Array de componentes de formulario mapeados
   */
  const extractTechComponents = async (
    admision: ICitaAdmision | undefined,
    estadosFiltro: string[] = ['TRAMITADO']
  ): Promise<IFormComponent[]> => {
    // Limpiar componentes previos
    components.value = []

    if (!admision?.technologias) {
      return components.value
    }

    // Filtrar tecnologías por estados especificados
    const technologiasFiltradas = admision.technologias.filter(
      tecnologia => estadosFiltro.includes(tecnologia.cittEstado || '')
    )

    if (technologiasFiltradas.length === 0) {
      return components.value
    }

    // Procesar cada tecnología filtrada
    for (const technologia of technologiasFiltradas) {
      try {
        if (!technologia.cittId) {
          console.warn('Tecnología sin cittId:', technologia)
          continue
        }

        // Obtener las tabs del servicio
        const { data } = await formCompValueService.document(technologia.cittId, { isForm: true, onlyTabs: true })
        const tabsWithComponentsForm = data.body.tabs

        if (!tabsWithComponentsForm || tabsWithComponentsForm.length === 0) {
          console.warn(`No se encontraron tabs para la tecnología ${technologia.cittId}`)
          continue
        }
        // Agregar componentes al array principal
        components.value.push(...tabsWithComponentsForm.map(tab => tab.components).flat().map(comp => ({ ...comp, codigo: technologia.cittCodTechnologia || technologia.contratoxservicio?.cup?.cocuCups || String(technologia.cittId), tecnologia: technologia.cittTechnologia })) as IFormComponent[])

      } catch (error) {
        console.error(`Error procesando tecnología ${technologia.cittId}:`, error)
      }
    }

    return components.value
  }

  /**
   * Reinicia el array de componentes
   */
  const clearComponents = () => {
    components.value = []
  }

  /**
   * Obtiene los componentes actuales
   */
  const getComponents = () => {
    return components.value
  }

  /**
   * Obtiene el número total de componentes
   */
  const getComponentsCount = () => {
    return components.value.length
  }

  /**
   * Busca componentes por código de tecnología
   */
  const getComponentsByTechCode = (codigo: string) => {
    return components.value.filter(component =>
      (component as IExtendedFormComponent).codigo === codigo
    )
  }

  /**
   * Busca componentes por tipo
   */
  const getComponentsByType = (type: string) => {
    return components.value.filter(component =>
      component.typeComponent === type
    )
  }

  return {
    // Estado reactivo
    components,

    // Métodos principales
    extractTechComponents,
    clearComponents,

    // Métodos de consulta
    getComponents,
    getComponentsCount,
    getComponentsByTechCode,
    getComponentsByType
  }
}
